import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useEffect, useMemo, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { range } from "lib/range"
import { WeekHeader } from "routes/plan/week-header"
import { PlannerItems } from "routes/plan/plugins/weekly-planner/plugins/planner-skills-schedule/planner-items"
import { PlannerDetails } from "routes/plan/planner-details"
import { ListItemBox } from "lib/@components/ListItemBox"
import { setFromEvent, setFromValueParam } from "lib/setFromEvent"
import { StandardCheckBox } from "lib/@components/standard-checkbox"
import { useReferenceState } from "lib/@hooks/use-reference"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { BoundToggleButtonGroup, StandardAutocomplete } from "lib/@components/binding/bound-components"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { getSkillsForTree } from "routes/plan/plugins/weekly-planner/plugins/planner-schedule-list/controller/get-skills-for-tree"
import GenericPagination from "lib/@components/paging/generic-pagination"
import { StandardCombo } from "lib/@components/combos/standardCombo"

export function PlanningWeekView(props) {
    const [state] = useState({})
    const refresh = useRefresh(noChange)
    return (
        <Bound target={state} refresh={refresh}>
            <InnerPlanningWeekView {...props} />
        </Bound>
    )
}

function InnerPlanningWeekView({ date: view }) {
    const activeDate = new Date(view)
    const firstDate = new Date(view).beginningOfMonth()
    const endDate = new Date(view).endOfMonth()
    const { days, treeId, year, setYear } = useBoundContext()
    const [showAssets, setShowAssets] = useBoundValue("showAssets", false)
    const [showVisit, setShowVisit] = useBoundValue("showVisit", true)
    const [showTime, setShowTime] = useBoundValue("showTime", false)
    const [page, setPage] = useState(1)

    const [type] = useBoundValue("type", "schedules")
    const [maxRows, setMaxRows] = useReferenceState("weekPlannerRows", 10)
    const [stickyColumns, setStickyColumns] = useReferenceState("stickyPlannerColumns", true)
    const [skills, setSkills] = useState([])

    const [, setSelectedSchedule] = useSearchParam(`selectedPlanSchedule`)

    useEffect(() => {
        setSelectedSchedule("")
    }, [setSelectedSchedule])

    useEffect(() => {
        setPage(1)
    }, [type, days, maxRows, skills])

    const skillOptions = (getSkillsForTree.useResults(treeId.split("!").at(0).replaceAll("~", "")) ?? [])
        .filter(Boolean)
        .map(({ id, skill: { Skilling: skill, ...props } }) => ({
            value: id,
            label: skill,
            ...props,
        }))
        .sortBy("label")

    const [filteredDays, allSchedules, allScheduleIndex] = useMemo(() => {
        const passedOnDays = structuredClone(days)
        for (const day of passedOnDays) {
            day.tasks = skills.length
                ? day.tasks.filter((c) => skills.find((s) => s.value == c.skillSet))
                : [...day.tasks]
            day.schedules.forEach(
                (schedule) =>
                    (schedule.tasks =
                        skills.length === 0
                            ? schedule.tasks
                            : schedule.tasks.filter((c) => skills.find((s) => s.SkillingCode == c.skillCode)))
            )
            day.schedules = day.schedules.filter((s) => s.tasks.length)
            day.schedules.forEach((schedule) => {
                const colors = schedule.tasks.reduce(
                    (a, c) => {
                        const classification = c.classification.toLowerCase()
                        return {
                            ...a,
                            [classification]: (a[classification] ?? 0) + 1,
                            minutes: (a.minutes ?? 0) + c.minutes ?? 0,
                        }
                    },
                    { red: 0, amber: 0, green: 0, pink: 0, minutes: 0 }
                )
                Object.assign(schedule, colors)
            })
            Object.assign(
                day,
                day.schedules.reduce(
                    (a, c) => ({
                        red: a.red + c.red,
                        green: a.green + c.green,
                        pink: a.pink + c.pink,
                        amber: a.amber + c.amber,
                        minutes: a.minutes + c.minutes,
                    }),
                    { red: 0, amber: 0, pink: 0, green: 0, minutes: 0 }
                )
            )
        }
        const allScheduleIndex = passedOnDays.reduce((a, r) => {
            for (const schedule of r.schedules) {
                a[schedule.scheduleId] = schedule
            }
            return a
        }, {})

        const allSchedules = Object.keys(allScheduleIndex)
            .sortBy((c) => allScheduleIndex[c].fullPath.split("-").slice(1).join("-"))
            .sortBy((c) => allScheduleIndex[c].fullPath.split("-")[0])

        return [passedOnDays, allSchedules, allScheduleIndex]
    }, [days, skills])

    const items = PlannerItems.useItems(filteredDays, stickyColumns, type, skills)

    return (
        <Bound
            skills={skills}
            stickyColumns={stickyColumns}
            allScheduleIndex={allScheduleIndex}
            allSchedules={allSchedules}
            days={filteredDays}
            date={activeDate}
            firstDate={firstDate}
            endDate={endDate}
        >
            <TableContainer>
                <Stack spacing={1} sx={{ width: 1, pt: 1 }}>
                    <ListItemBox spacing={1} sx={{ whiteSpace: "nowrap" }}>
                        <Box>
                            <BoundToggleButtonGroup
                                exclusive
                                field="type"
                                defaultValue="schedules"
                                color="primary"
                                size="small"
                            >
                                <ToggleButton value="schedules">Schedules</ToggleButton>
                                <ToggleButton value="skills">Skills</ToggleButton>
                            </BoundToggleButtonGroup>
                        </Box>
                        <ToggleButtonGroup exclusive={false} size="small" color="primary">
                            <ToggleButton selected={showAssets} onClick={() => setShowAssets((c) => !c)}>
                                Asset Count
                            </ToggleButton>

                            <ToggleButton selected={showVisit} onClick={() => setShowVisit((c) => !c)}>
                                Visit
                            </ToggleButton>
                            <ToggleButton selected={showTime} onClick={() => setShowTime((c) => !c)}>
                                Time
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <StandardAutocomplete
                            size="small"
                            color="primary"
                            label="Skill Filter"
                            sx={{ minWidth: 170 }}
                            options={skillOptions}
                            value={skills}
                            multiple
                            onChange={setFromValueParam(setSkills)}
                        />
                        <StandardCombo
                            size="small"
                            value={year}
                            onChange={setFromEvent((v) => setYear(v.value ?? v))}
                            fullWidth={false}
                            options={range(1, 20).map((i) => ({ label: `Year ${i}`, value: i - 1 }))}
                        />
                        <Box flex={1} />
                        <GenericPagination
                            changePage={setPage}
                            changeRowsPerPage={setMaxRows}
                            take={maxRows}
                            numberOfPages={Math.ceil(items.length / maxRows)}
                            currentPage={page}
                        />
                    </ListItemBox>
                    <Box width={1} overflow="auto">
                        <Table size="small">
                            <TableHead
                                sx={{
                                    "& th": {
                                        px: 1,
                                        py: 0.5,
                                    },
                                }}
                            >
                                <TableRow sx={{ position: "sticky", top: 0, zIndex: 8, bgcolor: "background.paper" }}>
                                    <TableCell
                                        sx={{
                                            position: stickyColumns ? "sticky" : "",
                                            bgcolor: "background.paper",
                                            left: 0,
                                            zIndex: 9,
                                        }}
                                    >
                                        <ListItemBox sx={{ minWidth: 510 }}>
                                            <Box flex={1}>
                                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                    Label
                                                </Typography>
                                            </Box>
                                            <StandardCheckBox
                                                sx={{ flex: 0, mr: 0.5 }}
                                                onChange={setFromEvent(setStickyColumns)}
                                                value={stickyColumns}
                                                label="Freeze"
                                            />
                                        </ListItemBox>
                                    </TableCell>
                                    <Repeat list={range(0, 51)} item={<WeekHeader />} />
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    "& td": {
                                        borderBottom: (theme) => "1px dotted " + theme.palette.action.selected,
                                        borderRight: (theme) => "1px dotted " + theme.palette.action.hover,
                                    },
                                }}
                            >
                                <PlannerDetails.Slot type={type} days={days} />
                                {items.slice((page - 1) * maxRows, page * maxRows)}
                            </TableBody>
                        </Table>
                    </Box>
                </Stack>
            </TableContainer>
        </Bound>
    )
}
